import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Plus, Minus, Trash2, PackageCheck, TruckIcon, Info } from 'lucide-react';
import ImageUpload from '../components/ImageUpload';
import { calculateVolume, estimateDistance, calculatePrice } from '../utils/calculators';
interface Package {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  images: File[];
}
interface OrderFormData {
  pickupAddress: string;
  pickupCity: string;
  deliveryAddress: string;
  deliveryCity: string;
  moveDate: Date | null;
  name: string;
  phone: string;
  email: string;
  packages: Package[];
  vehicleSize: 'small' | 'medium' | 'large';
  numberOfMovers: number;
  packingService: boolean;
  insurance: boolean;
  notes: string;
}
const OrderForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);
  const [formData, setFormData] = useState<OrderFormData>({
    pickupAddress: '',
    pickupCity: '',
    deliveryAddress: '',
    deliveryCity: '',
    moveDate: null,
    name: '',
    phone: '',
    email: '',
    packages: [{
      id: '1',
      name: '',
      length: 0,
      width: 0,
      height: 0,
      images: []
    }],
    vehicleSize: 'medium',
    numberOfMovers: 2,
    packingService: false,
    insurance: false,
    notes: ''
  });
  // Calculate total volume and price whenever relevant form fields change
  useEffect(() => {
    // Only calculate if we have enough information
    if (formData.pickupCity && formData.deliveryCity && formData.packages.some(pkg => pkg.length && pkg.width && pkg.height)) {
      // Calculate total volume
      const totalVolume = formData.packages.reduce((sum, pkg) => {
        return sum + calculateVolume(pkg.length, pkg.width, pkg.height);
      }, 0);
      // Estimate distance
      const distance = estimateDistance(formData.pickupCity, formData.deliveryCity);
      // Calculate price
      const price = calculatePrice(totalVolume, distance, formData.vehicleSize, formData.numberOfMovers, formData.packingService, formData.insurance);
      setTotalPrice(price);
    } else {
      setTotalPrice(0);
    }
  }, [formData]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };
  const handleDateChange = (date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      moveDate: date
    }));
  };
  const handlePackageChange = (packageId: string, field: keyof Package, value: any) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.map(pkg => pkg.id === packageId ? {
        ...pkg,
        [field]: value
      } : pkg)
    }));
  };
  const addPackage = () => {
    const newPackage: Package = {
      id: Date.now().toString(),
      name: '',
      length: 0,
      width: 0,
      height: 0,
      images: []
    };
    setFormData(prev => ({
      ...prev,
      packages: [...prev.packages, newPackage]
    }));
  };
  const removePackage = (packageId: string) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.filter(pkg => pkg.id !== packageId)
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, you would send this data to a server
    // For this demo, we'll just store it in localStorage and navigate to confirmation
    localStorage.setItem('moveOrder', JSON.stringify({
      ...formData,
      totalPrice,
      orderNumber: `EST-${Date.now().toString().slice(-6)}`,
      status: 'Scheduled'
    }));
    navigate('/confirmation');
  };
  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  return <div className="bg-gray-50 py-10 min-h-screen" data-id="element-223">
      <div className="container mx-auto px-4" data-id="element-224">
        <div className="max-w-4xl mx-auto" data-id="element-225">
          <h1 className="text-3xl font-bold text-center mb-8" data-id="element-226">
            Book Your Move
          </h1>
          {/* Progress Steps */}
          <div className="flex justify-between mb-8" data-id="element-227">
            <div className={`flex-1 text-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-228">
              <div className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center mb-2 ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-229">
                1
              </div>
              <span className="text-sm" data-id="element-230">Addresses</span>
            </div>
            <div className={`flex-1 text-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-231">
              <div className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center mb-2 ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-232">
                2
              </div>
              <span className="text-sm" data-id="element-233">Items & Services</span>
            </div>
            <div className={`flex-1 text-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-234">
              <div className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center mb-2 ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-235">
                3
              </div>
              <span className="text-sm" data-id="element-236">Contact & Review</span>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6" data-id="element-237">
            {/* Step 1: Addresses */}
            {currentStep === 1 && <div className="space-y-6" data-id="element-238">
                <h2 className="text-xl font-semibold mb-4" data-id="element-239">
                  Pickup & Delivery Addresses
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-240">
                  <div data-id="element-241">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-242">
                      Pickup Street Address
                    </label>
                    <input type="text" name="pickupAddress" value={formData.pickupAddress} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-id="element-243" />
                  </div>
                  <div data-id="element-244">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-245">
                      Pickup City
                    </label>
                    <input type="text" name="pickupCity" value={formData.pickupCity} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-id="element-246" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-247">
                  <div data-id="element-248">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-249">
                      Delivery Street Address
                    </label>
                    <input type="text" name="deliveryAddress" value={formData.deliveryAddress} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-id="element-250" />
                  </div>
                  <div data-id="element-251">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-252">
                      Delivery City
                    </label>
                    <input type="text" name="deliveryCity" value={formData.deliveryCity} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-id="element-253" />
                  </div>
                </div>
                <div data-id="element-254">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-255">
                    Preferred Moving Date
                  </label>
                  <DatePicker selected={formData.moveDate} onChange={handleDateChange} minDate={new Date()} dateFormat="dd/MM/yyyy" className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholderText="Select a date" required data-id="element-256" />
                </div>
                <div className="flex justify-end" data-id="element-257">
                  <button type="button" onClick={nextStep} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700" data-id="element-258">
                    Next
                  </button>
                </div>
              </div>}
            {/* Step 2: Items & Services */}
            {currentStep === 2 && <div className="space-y-6" data-id="element-259">
                <h2 className="text-xl font-semibold mb-4" data-id="element-260">Items & Services</h2>
                {/* Packages */}
                <div data-id="element-261">
                  <h3 className="text-lg font-medium mb-3" data-id="element-262">Your Items</h3>
                  {formData.packages.map((pkg, index) => <div key={pkg.id} className="mb-6 p-4 border border-gray-200 rounded-lg" data-id="element-263">
                      <div className="flex justify-between items-center mb-3" data-id="element-264">
                        <h4 className="font-medium" data-id="element-265">Item #{index + 1}</h4>
                        {formData.packages.length > 1 && <button type="button" onClick={() => removePackage(pkg.id)} className="text-red-500 hover:text-red-700" data-id="element-266">
                            <Trash2 className="h-5 w-5" data-id="element-267" />
                          </button>}
                      </div>
                      <div className="mb-3" data-id="element-268">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-269">
                          Item Name
                        </label>
                        <input type="text" value={pkg.name} onChange={e => handlePackageChange(pkg.id, 'name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="e.g. Sofa, Refrigerator" required data-id="element-270" />
                      </div>
                      <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-271">
                        <div data-id="element-272">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-273">
                            Length (cm)
                          </label>
                          <input type="number" value={pkg.length || ''} onChange={e => handlePackageChange(pkg.id, 'length', Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md" min="1" required data-id="element-274" />
                        </div>
                        <div data-id="element-275">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-276">
                            Width (cm)
                          </label>
                          <input type="number" value={pkg.width || ''} onChange={e => handlePackageChange(pkg.id, 'width', Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md" min="1" required data-id="element-277" />
                        </div>
                        <div data-id="element-278">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-279">
                            Height (cm)
                          </label>
                          <input type="number" value={pkg.height || ''} onChange={e => handlePackageChange(pkg.id, 'height', Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md" min="1" required data-id="element-280" />
                        </div>
                      </div>
                      <div data-id="element-281">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-282">
                          Upload Photos (optional)
                        </label>
                        <ImageUpload maxImages={3} onImagesChange={images => handlePackageChange(pkg.id, 'images', images)} data-id="element-283" />
                      </div>
                    </div>)}
                  <button type="button" onClick={addPackage} className="flex items-center text-blue-600 font-medium" data-id="element-284">
                    <Plus className="h-5 w-5 mr-1" data-id="element-285" />
                    Add Another Item
                  </button>
                </div>
                {/* Service Options */}
                <div className="mt-8" data-id="element-286">
                  <h3 className="text-lg font-medium mb-3" data-id="element-287">Service Options</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-288">
                    <div data-id="element-289">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-290">
                        Vehicle Size
                      </label>
                      <select name="vehicleSize" value={formData.vehicleSize} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-291">
                        <option value="small" data-id="element-292">Small (up to 10m³)</option>
                        <option value="medium" data-id="element-293">Medium (up to 20m³)</option>
                        <option value="large" data-id="element-294">Large (up to 35m³)</option>
                      </select>
                    </div>
                    <div data-id="element-295">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-296">
                        Number of Movers
                      </label>
                      <div className="flex items-center" data-id="element-297">
                        <button type="button" onClick={() => setFormData(prev => ({
                      ...prev,
                      numberOfMovers: Math.max(1, prev.numberOfMovers - 1)
                    }))} className="px-3 py-2 border border-gray-300 rounded-l-md" data-id="element-298">
                          <Minus className="h-4 w-4" data-id="element-299" />
                        </button>
                        <input type="number" value={formData.numberOfMovers} readOnly className="w-16 text-center border-t border-b border-gray-300 py-2" data-id="element-300" />
                        <button type="button" onClick={() => setFormData(prev => ({
                      ...prev,
                      numberOfMovers: Math.min(4, prev.numberOfMovers + 1)
                    }))} className="px-3 py-2 border border-gray-300 rounded-r-md" data-id="element-301">
                          <Plus className="h-4 w-4" data-id="element-302" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3" data-id="element-303">
                    <div className="flex items-center" data-id="element-304">
                      <input type="checkbox" id="packingService" name="packingService" checked={formData.packingService} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-305" />
                      <label htmlFor="packingService" className="ml-2 text-gray-700" data-id="element-306">
                        Professional Packing Service
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-307">
                      <input type="checkbox" id="insurance" name="insurance" checked={formData.insurance} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-308" />
                      <label htmlFor="insurance" className="ml-2 text-gray-700" data-id="element-309">
                        Moving Insurance
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between" data-id="element-310">
                  <button type="button" onClick={prevStep} className="text-blue-600 px-6 py-2 rounded-lg hover:bg-blue-50" data-id="element-311">
                    Back
                  </button>
                  <button type="button" onClick={nextStep} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700" data-id="element-312">
                    Next
                  </button>
                </div>
              </div>}
            {/* Step 3: Contact & Review */}
            {currentStep === 3 && <div className="space-y-6" data-id="element-313">
                <h2 className="text-xl font-semibold mb-4" data-id="element-314">
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-315">
                  <div data-id="element-316">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-317">
                      Full Name
                    </label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-id="element-318" />
                  </div>
                  <div data-id="element-319">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-320">
                      Phone Number
                    </label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-id="element-321" />
                  </div>
                </div>
                <div data-id="element-322">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-323">
                    Email Address
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-id="element-324" />
                </div>
                <div data-id="element-325">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-326">
                    Additional Notes (optional)
                  </label>
                  <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-327" />
                </div>
                <div className="mt-8" data-id="element-328">
                  <h3 className="text-lg font-medium mb-3" data-id="element-329">Order Summary</h3>
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-330">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-331">
                      <div data-id="element-332">
                        <p className="text-sm text-gray-500 mb-1" data-id="element-333">From</p>
                        <p className="font-medium" data-id="element-334">
                          {formData.pickupAddress}, {formData.pickupCity}
                        </p>
                      </div>
                      <div data-id="element-335">
                        <p className="text-sm text-gray-500 mb-1" data-id="element-336">To</p>
                        <p className="font-medium" data-id="element-337">
                          {formData.deliveryAddress}, {formData.deliveryCity}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-338">
                      <p className="text-sm text-gray-500 mb-1" data-id="element-339">Moving Date</p>
                      <p className="font-medium" data-id="element-340">
                        {formData.moveDate?.toLocaleDateString()}
                      </p>
                    </div>
                    <div className="mt-4" data-id="element-341">
                      <p className="text-sm text-gray-500 mb-1" data-id="element-342">Services</p>
                      <ul className="list-disc list-inside" data-id="element-343">
                        <li data-id="element-344">
                          {formData.vehicleSize === 'small' ? 'Small' : formData.vehicleSize === 'medium' ? 'Medium' : 'Large'}{' '}
                          vehicle
                        </li>
                        <li data-id="element-345">
                          {formData.numberOfMovers}{' '}
                          {formData.numberOfMovers === 1 ? 'mover' : 'movers'}
                        </li>
                        {formData.packingService && <li data-id="element-346">Packing service</li>}
                        {formData.insurance && <li data-id="element-347">Moving insurance</li>}
                      </ul>
                    </div>
                    <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-348">
                      <div className="flex justify-between items-center" data-id="element-349">
                        <span className="font-semibold" data-id="element-350">Estimated Total:</span>
                        <span className="text-xl font-bold" data-id="element-351">€{totalPrice}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-352">
                        Final price may vary based on actual weight and distance
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between" data-id="element-353">
                  <button type="button" onClick={prevStep} className="text-blue-600 px-6 py-2 rounded-lg hover:bg-blue-50" data-id="element-354">
                    Back
                  </button>
                  <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center" data-id="element-355">
                    <TruckIcon className="mr-2 h-5 w-5" data-id="element-356" />
                    Complete Booking
                  </button>
                </div>
              </div>}
          </form>
        </div>
      </div>
    </div>;
};
export default OrderForm;